/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {isArrayEmpty} from '@ion/core/src/utils/ArrayUtil';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {PREFERENCE_NAME} from './ModularSearchWidget';
import {SavedQueryType} from './SaveQueriesList';
import {SearchScopeValues} from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export {SearchScopeValues} from './types';

interface SearchScopeProps {
  queryAttribute: string;
  setIsExact?: Function;
  isExact?: boolean;
  pageName: string;
};

const useStyles = makeStyles(() => {
  const toggleChip = {
    ...dlsAlt('typography', 'cta2'),
    borderRadius : 4,
    padding      : '4px 8px',
    cursor       : 'pointer'
  };
  return {
    exactToggleChip : {
      ...toggleChip,
      color      : dlsColor('caGreen'),
      background : `${dlsColor('caGreen')}20`
    },
    partialToggleChip : {
      ...toggleChip,
      color      : dlsColor('grey3'),
      background : dlsColor('grey6')
    }
  };
});

export const hasValidSelection = (queryAttribute: string): boolean => {
  switch (queryAttribute) {
    case SearchScopeValues.FIRMWARE_VERSION:
    case SearchScopeValues.CONFIG_VERSION:
    case SearchScopeValues.JOBS:
      return true;
    default :
      return false;
  };
};

export const isValidSelection = (queryAttribute: string, pageName: string): boolean => {
  if (hasValidSelection(queryAttribute) && (isEqual(SearchScopeValues.DEVICES, pageName) ||
  isEqual(SearchScopeValues.JOBS, pageName))) {
    return true;
  }
  return false;
};

export const getSavedSearchScopeForPage = (
  latestQueriesForPage: SavedQueryType, queryAttribute: string, isExact: boolean): boolean => {
  if (isEqual(SearchScopeValues.FIRMWARE_VERSION, queryAttribute)) {
    return hasQueryKeyForPage(latestQueriesForPage, SearchScopeValues.FIRMWARE_VERSION);
  } if (isEqual(SearchScopeValues.CONFIG_VERSION, queryAttribute)) {
    return hasQueryKeyForPage(latestQueriesForPage, SearchScopeValues.CONFIG_VERSION);
  } if (isEqual(SearchScopeValues.JOBS, queryAttribute)) {
    return isExact;
  }
  return false;
};

const getSavedQueriesForPage = (pageName: string, isToggleClick: boolean = false) => {
  const userPreferences = UserPreferences.get(PREFERENCE_NAME) ?? [];
  if (isToggleClick) {
    return userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
  }
  return !isArrayEmpty(userPreferences)
    ? userPreferences?.filter(({pageName: name}) => name === pageName)[0] ?? {}
    : {};
};

const hasQueryKeyForPage = (latestQueriesForPage: SavedQueryType, key: string): boolean => {
  return latestQueriesForPage?.query?.find(({name}) => name === key)?.query === SearchScopeValues.EXACT;
};

/**
 * @SearchScope
 * Is a component that allows the user to search values partial/exact match.
 *
 * ```typescript
 * import SearchScope from './SearchScope';
 *
 * <SearchScope queryAttribute={queryAttribute}
 *   pageName={pageName} />
 *
 * ```
 *
 */
const SearchScope = ({
  pageName,
  setIsExact,
  queryAttribute,
  isExact = false
}: SearchScopeProps) => {
  const latestQueriesForPage = getSavedQueriesForPage(pageName);
  const isExactSearch        = getSavedSearchScopeForPage(latestQueriesForPage, queryAttribute, isExact);
  const classes              = useStyles();
  const {
    on,
    off,
    exact
  } = translate('common:modularsearch.searchScope', {returnObjects: true});

  const onToggleClick = useCallback(() => {
    return async(event?: any) => {
      event?.preventDefault();
      if (pageName === SearchScopeValues.JOBS) {
        setIsExact(!isExact);
      } else {
        const otherPageQueries = getSavedQueriesForPage(pageName, true);
        const queries          = isExactSearch ? SearchScopeValues.PARTIAL : SearchScopeValues.EXACT;
        const savedQueryIndex  = latestQueriesForPage?.query?.findIndex(({name}) => queryAttribute === name);
        if (savedQueryIndex >= 0) {
          latestQueriesForPage.query[savedQueryIndex].query = queries;
        } else {
          latestQueriesForPage.query.push({
            name  : queryAttribute,
            query : queries
          });
        }
        await UserPreferences.set(
          PREFERENCE_NAME,
          [...otherPageQueries, latestQueriesForPage],
          !UserPreferences.hasChangeDetection()
        );
      }
    };
  }, [queryAttribute, pageName, isExactSearch, latestQueriesForPage, isExact, setIsExact]);

  return (
    <div className={isExactSearch ? classes.exactToggleChip : classes.partialToggleChip}
      onClick={onToggleClick()}>
      <Tooltip arrow title={isExactSearch ? on : off}>
        <span>
          {exact}
        </span>
      </Tooltip>
    </div>
  );
};

export default SearchScope;
